import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function CookieBanner({
  policyPath = '/privacy',
  storageKey = 'cookieConsent',
  version = '3',
  className = ''
}) {
  const [visible, setVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        setVisible(true);
        return;
      }
      const parsed = JSON.parse(stored);

      // Show banner if:
      // - no explicit choice yet (accepted is not true and rejected is not true)
      // - or the consent schema/version changed
      const hasChoice = parsed?.accepted === true || parsed?.rejected === true;
      if (!hasChoice || parsed?.version !== version) {
        setVisible(true);
      }
    } catch {
      // If localStorage parsing fails, show the banner to be safe
      setVisible(true);
    }
  }, [storageKey, version]);

  useEffect(() => {
    const onResize = () => setIsMobile(typeof window !== 'undefined' && window.innerWidth <= 560);
    onResize();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const setConsentCookie = (value) => {
    try {
      // 12 months expiry
      const maxAge = 60 * 60 * 24 * 365;
      document.cookie = `cookie_consent=${value}; Path=/; Max-Age=${maxAge}; SameSite=Lax`;
    } catch {
      // no-op
    }
  };

  const handleAccept = () => {
    const record = { accepted: true, rejected: false, ts: new Date().toISOString(), version };
    try {
      localStorage.setItem(storageKey, JSON.stringify(record));
    } catch {
      // no-op
    }
    setConsentCookie('accepted');
    setVisible(false);

    // Notify the app (e.g. pages that want to lazily load embeds)
    window.dispatchEvent(new Event('cookie-consent-changed'));
  };

  const handleReject = () => {
    const record = { accepted: false, rejected: true, ts: new Date().toISOString(), version };
    try {
      localStorage.setItem(storageKey, JSON.stringify(record));
    } catch {
      // no-op
    }
    setConsentCookie('rejected');
    setVisible(false);

    // Notify the app (e.g. pages that want to lazily load embeds)
    window.dispatchEvent(new Event('cookie-consent-changed'));
  };

  if (!visible) return null;

  // Read CSS variables safely (fallbacks for SSR or missing vars)
  const getCSSVar = (name, fallback) => {
    try {
      const v = getComputedStyle(document.documentElement).getPropertyValue(name);
      return (v || '').trim() || fallback;
    } catch {
      return fallback;
    }
  };

  const theme = {
    background: getCSSVar('--color4', '#1f2b3a'),
    color: getCSSVar('--text-color', '#ffffff'),
    accent: getCSSVar('--color4', '#2a6f9e'),
    lightTextColor: getCSSVar('--light-text-color', '#ffffff'),
    borderColor: getCSSVar('--border-color', 'rgba(0,0,0,0.12)'),
    color3: getCSSVar('--color3', 'rgba(0,0,0,0.08)')
  };

  const mergedContentStyle = {
    ...contentStyle,
    gridTemplateColumns: isMobile ? '1fr' : contentStyle.gridTemplateColumns,
    padding: isMobile ? '0.75rem' : contentStyle.padding,
    gap: isMobile ? '0.5rem' : contentStyle.gap,
    background: theme.background,
    color: theme.color,
    border: `1px solid ${theme.borderColor}`
  };

  const mergedTextStyle = {
    ...textStyle,
    fontSize: isMobile ? '0.9rem' : textStyle.fontSize,
    color: theme.color
  };

  const mergedActionsStyle = {
    ...actionsStyle,
    justifyContent: isMobile ? 'center' : actionsStyle.justifyContent,
    gap: isMobile ? '0.5rem' : actionsStyle.gap,
    paddingTop: isMobile ? '0.5rem' : undefined
  };

  const mergedAcceptBtnStyle = {
    ...acceptBtnStyle,
    padding: isMobile ? '0.5rem 0.85rem' : acceptBtnStyle.padding,
    fontSize: isMobile ? '0.95rem' : undefined,
    background: theme.accent,
    color: theme.lightTextColor,
    border: 'none'
  };

  const mergedRejectBtnStyle = {
    ...rejectBtnStyle,
    padding: isMobile ? '0.5rem 0.85rem' : rejectBtnStyle.padding,
    fontSize: isMobile ? '0.95rem' : undefined,
    color: theme.color,
    border: `1px solid ${theme.color3}`
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie notice"
      className={className}
      style={containerStyle}
    >
      <div style={mergedContentStyle}>
        <div style={mergedTextStyle}>
          We use cookies and similar technologies to keep PeaksOnFoot running. Accepting enables anonymous Google Analytics and Firebase Performance Monitoring plus optional third-party embeds (like Google Maps) that can set their own cookies. We always run Google reCAPTCHA App Check for security, but it only places strictly necessary cookies. Rejecting keeps everything else essential only.
          Learn how we use data in our{' '}
          <Link to={policyPath} style={{ ...linkStyle, color: theme.accent }}>
            Privacy Policy
          </Link>.
        </div>
        <div style={mergedActionsStyle}>
          <button onClick={handleReject} style={mergedRejectBtnStyle}>
            Reject
          </button>
          <button onClick={handleAccept} style={mergedAcceptBtnStyle}>
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

const containerStyle = {
  position: 'fixed',
  left: '1rem',
  right: '1rem',
  bottom: '1rem',
  zIndex: 2147483647, // on top of most elements
  display: 'flex',
  justifyContent: 'center',
  pointerEvents: 'none' // allow clicks only on the banner box, not the overlay
};

const contentStyle = {
  pointerEvents: 'auto',
  width: '100%',
  maxWidth: '800px',
  border: '1px solid var(--border-color)',
  borderRadius: '10px',
  background: 'var(--color4)',
  color: 'var(--bg-color)',
  boxShadow: '0 12px 24px rgba(0,0,0,0.18)',
  padding: '1rem',
  display: 'grid',
  gridTemplateColumns: '1fr auto',
  gap: '0.75rem'
};

const textStyle = {
  textAlign: 'left',
  lineHeight: 1.4,
  fontSize: '0.95rem'
};

const actionsStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '0.5rem'
};

const acceptBtnStyle = {
  background: 'var(--color4)',
  color: 'var(--bg-color)',
  border: 'none',
  borderRadius: '8px',
  padding: '0.6rem 1rem',
  cursor: 'pointer',
  fontWeight: 600
};

const rejectBtnStyle = {
  background: 'transparent',
  color: 'var(--bg-color)',
  border: '1px solid var(--color3)',
  borderRadius: '8px',
  padding: '0.6rem 1rem',
  cursor: 'pointer',
  fontWeight: 600
};

const linkStyle = {
  color: 'var(--color4)',
  textDecoration: 'underline'
};
